import * as React from 'react';
import { useMemo, useState } from 'react';
import {
    Box, Button, InputAdornment, Stack, TextField, ThemeProvider, Typography
} from '@mui/material';
import { generateTheme } from '../themes';
import { detectLang, DEFAULT_LANG } from '../utils/language';

const SearchIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const PlusGlyph = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const CloseGlyph = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const stripHtml = (s) => (s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const Pill = ({ active, onClick, children }) => (
    <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '999px',
            px: 2.25,
            py: 1.1,
            fontSize: '0.875rem',
            fontWeight: 500,
            border: '1px solid',
            borderColor: active ? '#16121C' : '#E2DDD5',
            background: active ? '#16121C' : 'transparent',
            color: active ? '#fff' : '#16121C',
            transition: 'background 120ms ease, color 120ms ease',
            '&:hover': active ? {} : { background: 'rgba(22, 18, 28, 0.04)' },
            '&:focus-visible': { outline: '2px solid #5C33D7', outlineOffset: 2 }
        }}
    >
        {children}
    </Box>
);

const buttonBaseSx = {
    borderRadius: '10px',
    py: 1.5,
    px: 3,
    fontSize: '0.9375rem',
    fontWeight: 600,
    textTransform: 'none',
    fontFamily: 'Roboto, sans-serif',
    boxShadow: 'none',
    minHeight: 48,
    '&:hover': { boxShadow: 'none' }
};

const primaryBtnSx = {
    ...buttonBaseSx,
    backgroundColor: '#5C33D7',
    color: '#fff',
    '&:hover': { backgroundColor: '#4C1D95', boxShadow: 'none' }
};

const secondaryBtnSx = {
    ...buttonBaseSx,
    backgroundColor: 'transparent',
    color: '#16121C',
    border: '1.5px solid #16121C',
    '&:hover': { backgroundColor: 'rgba(22, 18, 28, 0.04)', boxShadow: 'none' }
};

const resolveUrl = (link) => {
    if (!link) return '#';
    if (typeof link === 'string') return link || '#';
    return link?.url || '#';
};

const Faq = ({ data }) => {
    const {
        colorTheme = 'off-white',
        items = [],
        eyebrowHeadline = '',
        title = '',
        description = '',
        postHeadline = '',
        postDescription = '',
        buttonLink = '',
        buttonText = '',
        secondaryButtonLink = '',
        secondaryButtonText = ''
    } = data || {};

    const theme = generateTheme(colorTheme);

    const isEnglish = typeof window !== 'undefined'
        && detectLang(window.location.pathname) !== DEFAULT_LANG;

    const [query, setQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [expandedKey, setExpandedKey] = useState(null);

    const categories = useMemo(() => {
        const seen = new Set();
        const list = [];
        items.forEach((it) => {
            const cat = stripHtml(it.questionHeadline);
            if (cat && !seen.has(cat)) {
                seen.add(cat);
                list.push(cat);
            }
        });
        return list;
    }, [items]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((it) => {
            const cat = stripHtml(it.questionHeadline);
            if (activeCategory !== 'all' && cat !== activeCategory) return false;
            if (!q) return true;
            const haystack = (stripHtml(it.question) + ' ' + stripHtml(it.answer)).toLowerCase();
            return haystack.includes(q);
        });
    }, [items, query, activeCategory]);

    const groups = useMemo(() => {
        const map = new Map();
        const order = [];
        filtered.forEach((it, originalIdx) => {
            const cat = stripHtml(it.questionHeadline) || 'Övrigt';
            if (!map.has(cat)) {
                map.set(cat, []);
                order.push(cat);
            }
            map.get(cat).push({ ...it, _idx: items.indexOf(it) });
        });
        return order.map((cat) => ({ category: cat, items: map.get(cat) }));
    }, [filtered, items]);

    const showHelpBox = postHeadline || postDescription || buttonText || secondaryButtonText;

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 3, md: 7.5 },
                    pt: { xs: 5, md: 5 },
                    pb: { xs: 8, md: 10 },
                    width: '100%',
                    maxWidth: '1200px',
                    mx: 'auto',
                    boxSizing: 'border-box',
                    overflowX: 'hidden'
                }}
            >
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Header */}
                    {(eyebrowHeadline || title) && (
                        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                            {eyebrowHeadline && (
                                <>
                                    <Typography
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            color: 'text.secondary',
                                            letterSpacing: '0.2em',
                                            textTransform: 'uppercase',
                                            fontSize: '0.8rem',
                                            fontWeight: 500,
                                            opacity: 0.7,
                                            mb: 1
                                        }}
                                        dangerouslySetInnerHTML={{ __html: eyebrowHeadline }}
                                    />
                                    <Box
                                        aria-hidden="true"
                                        sx={{
                                            height: '4px',
                                            width: 160,
                                            borderRadius: '2px',
                                            mx: 'auto',
                                            mb: { xs: 2.5, md: 3 },
                                            background: 'linear-gradient(90deg, rgba(92, 51, 215, 0) 0%, rgb(92, 51, 215) 100%)'
                                        }}
                                    />
                                </>
                            )}
                            {title && (
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: 'Kanit, sans-serif',
                                        fontWeight: 900,
                                        fontSize: { xs: '40px', md: '64px' },
                                        lineHeight: 0.95,
                                        letterSpacing: '-1.5px',
                                        textTransform: 'uppercase',
                                        color: 'text.primary',
                                        m: 0,
                                        mb: description ? 2 : 0
                                    }}
                                    dangerouslySetInnerHTML={{ __html: title }}
                                />
                            )}
                            {description && (
                                <Typography
                                    component="p"
                                    sx={{
                                        fontSize: '17px',
                                        color: 'text.secondary',
                                        maxWidth: 640,
                                        lineHeight: 1.5,
                                        mx: 'auto',
                                        m: 0
                                    }}
                                    dangerouslySetInnerHTML={{ __html: description }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Search */}
                    <TextField
                        fullWidth
                        placeholder={isEnglish ? 'Search questions…' : 'Sök bland frågor...'}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Box sx={{ color: 'text.secondary', display: 'flex', pl: 0.5 }}>
                                        <SearchIcon size={20} />
                                    </Box>
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '999px',
                                backgroundColor: '#fff',
                                fontSize: '1rem',
                                py: 0.5,
                                pl: 1,
                                '& > fieldset': { borderColor: '#E2DDD5' },
                                '&:hover > fieldset': { borderColor: '#C8C0B3' },
                                '&.Mui-focused > fieldset': { borderColor: '#5C33D7', borderWidth: 1.5 }
                            }
                        }}
                    />

                    {/* Filter pills */}
                    {categories.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: { xs: 4, md: 5 } }}>
                            <Pill active={activeCategory === 'all'} onClick={() => setActiveCategory('all')}>
                                {isEnglish ? 'All' : 'Alla'}
                            </Pill>
                            {categories.map((cat) => (
                                <Pill
                                    key={cat}
                                    active={activeCategory === cat}
                                    onClick={() => setActiveCategory(cat)}
                                >
                                    {cat}
                                </Pill>
                            ))}
                        </Box>
                    )}

                    {/* Categorized list */}
                    {groups.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            Inga frågor matchade din sökning.
                        </Box>
                    ) : (
                        groups.map((g, gi) => (
                            <Box key={`${g.category}-${gi}`} sx={{ mb: { xs: 4, md: 5 } }}>
                                <Typography
                                    component="h3"
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.18em',
                                        textTransform: 'uppercase',
                                        color: 'text.secondary',
                                        opacity: 0.6,
                                        m: 0,
                                        mb: 1.5
                                    }}
                                >
                                    {g.category}
                                </Typography>
                                <Box sx={{ borderTop: '0.5px solid #E2DDD5' }}>
                                    {g.items.map((it) => {
                                        const key = `${g.category}-${it._idx}`;
                                        const isOpen = expandedKey === key;
                                        return (
                                            <Box
                                                key={key}
                                                sx={{ borderBottom: '0.5px solid #E2DDD5' }}
                                            >
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    onClick={() => setExpandedKey(isOpen ? null : key)}
                                                    aria-expanded={isOpen}
                                                    sx={{
                                                        all: 'unset',
                                                        boxSizing: 'border-box',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        py: 2.25,
                                                        width: '100%',
                                                        '&:focus-visible': {
                                                            outline: '2px solid #5C33D7',
                                                            outlineOffset: 2
                                                        }
                                                    }}
                                                >
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontSize: { xs: '1rem', md: '1.0625rem' },
                                                            fontWeight: 600,
                                                            color: 'text.primary',
                                                            lineHeight: 1.4,
                                                            textAlign: 'left',
                                                            flex: 1
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: it.question }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            flexShrink: 0,
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: '50%',
                                                            background: isOpen ? '#5C33D7' : 'rgba(22, 18, 28, 0.08)',
                                                            color: isOpen ? '#fff' : '#16121C',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'background 120ms ease, color 120ms ease'
                                                        }}
                                                    >
                                                        {isOpen ? <CloseGlyph /> : <PlusGlyph />}
                                                    </Box>
                                                </Box>
                                                {isOpen && (
                                                    <Box
                                                        sx={{
                                                            pb: 3,
                                                            pr: { xs: 0, md: 6 },
                                                            color: 'text.primary',
                                                            fontSize: { xs: '0.9375rem', md: '1rem' },
                                                            lineHeight: 1.6,
                                                            '& p': { m: 0, mb: 1.5 },
                                                            '& p:last-child': { mb: 0 },
                                                            '& a': { color: '#5C33D7' }
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: it.answer }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))
                    )}

                    {/* Help / CTA box */}
                    {showHelpBox && (
                        <Box
                            sx={{
                                mt: { xs: 5, md: 8 },
                                background: 'rgba(22, 18, 28, 0.05)',
                                borderRadius: '24px',
                                p: { xs: 4, md: 6 },
                                textAlign: 'center'
                            }}
                        >
                            {postHeadline && (
                                <Typography
                                    component="h3"
                                    sx={{
                                        fontFamily: 'Kanit, sans-serif',
                                        fontWeight: 800,
                                        fontSize: { xs: '1.375rem', md: '1.75rem' },
                                        color: 'text.primary',
                                        m: 0,
                                        mb: postDescription ? 1.25 : 2.5
                                    }}
                                    dangerouslySetInnerHTML={{ __html: postHeadline }}
                                />
                            )}
                            {postDescription && (
                                <Typography
                                    component="p"
                                    sx={{
                                        fontSize: '1rem',
                                        color: 'text.secondary',
                                        lineHeight: 1.5,
                                        maxWidth: 540,
                                        mx: 'auto',
                                        m: 0,
                                        mb: 2.5
                                    }}
                                    dangerouslySetInnerHTML={{ __html: postDescription }}
                                />
                            )}
                            {(buttonText || secondaryButtonText) && (
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    {buttonText && (
                                        <Button href={resolveUrl(buttonLink)} disableElevation sx={primaryBtnSx}>
                                            {buttonText}
                                        </Button>
                                    )}
                                    {secondaryButtonText && (
                                        <Button href={resolveUrl(secondaryButtonLink)} disableElevation sx={secondaryBtnSx}>
                                            {secondaryButtonText}
                                        </Button>
                                    )}
                                </Stack>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default Faq;
